import { FC, ReactNode, SVGProps } from 'react'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-content-analysis.svg'
import { ReactComponent as CampaignIcon } from 'src/assets/svg/camp-card.svg'
import { ReactComponent as CommentIcon } from 'src/assets/svg/comment.svg'
import { ReactComponent as ContentTypeIcon } from 'src/assets/svg/content-analysis.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { ReactComponent as NewspaperIcon } from 'src/assets/svg/newspaper.svg'
import { Skeleton } from 'src/components/Skeleton'
import { DeltaBadge } from 'src/charts/TopicsSubtopics/DeltaBadge'
import { SentimentBar } from 'src/charts/TopicsSubtopics/SentimentBar'
import { SentimentHoverCard } from 'src/charts/TopicsSubtopics/SentimentHoverCard'
import { ContentAnalysisV2Range } from 'src/hooks/useContentAnalysisV2Range'
import { useGetContentAnalysisV2OverviewQuery } from 'src/reactQuery/useGetContentAnalysisV2OverviewQuery'
import {
  ContentOverviewV2,
  ContentPlatformCount,
  ContentTopPlatform,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'
import styles from './overview.module.scss'
import {
  SCORE_SENTENCE,
  SENTIMENT_COLORS,
  formatCount,
  percent,
  platformColor,
  platformLabel,
  scoreBand,
} from '../shared'

interface ContentAnalysisOverviewProps {
  // One id on the detail page; omitted on the list page (every analysis).
  analysisIds?: string[]
  // List page only: the "Campaigns 5 active / 12 total" header text.
  campaigns?: { active: number; total: number }
  range: ContentAnalysisV2Range
  // The list page shows Top Campaign, the detail page Top Content Type.
  variant: 'detail' | 'list'
}

type Icon = FC<SVGProps<SVGSVGElement>>

const BAND_CLASS = {
  Neutral: styles.bandNeutral,
  Poor: styles.bandPoor,
  Positive: styles.bandPositive,
}

// Gauge: a 150px ring, the score as the filled share of it.
const GAUGE_SIZE = 150
const GAUGE_STROKE = 14
const GAUGE_RADIUS = (GAUGE_SIZE - GAUGE_STROKE) / 2
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS

// Comments donut: 132px ring.
const DONUT_SIZE = 132
const DONUT_STROKE = 16
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS
const DONUT_GAP = 3

const SentimentFaceIcon: Icon = (props) => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24" {...props}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8.5 14.5s1.5 2 3.5 2 3.5-2 3.5-2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.5"
    />
    <circle cx="9" cy="10" fill="currentColor" r="1" />
    <circle cx="15" cy="10" fill="currentColor" r="1" />
  </svg>
)

const CardTitle: FC<{ children?: ReactNode; icon: Icon; title: string }> = ({
  children,
  icon: TitleIcon,
  title,
}) => (
  <div className={styles.cardHeader}>
    <TitleIcon className={styles.cardIcon} />
    <h3 className={styles.cardTitle}>{title}</h3>
    {children}
  </div>
)

const Delta: FC<{ suffix?: string; value: number | null | undefined }> = ({
  suffix,
  value,
}) =>
  value === null || value === undefined ? null : (
    <span
      className={`${styles.deltaPill} ${value < 0 ? styles.deltaPillDown : ''}`}
    >
      <DeltaBadge suffix={suffix} value={value} />
    </span>
  )

const Gauge: FC<{ score: number }> = ({ score }) => {
  const clamped = Math.max(0, Math.min(100, score))
  const dash = (clamped / 100) * GAUGE_CIRCUMFERENCE
  return (
    <div className={styles.gauge}>
      <svg
        aria-hidden="true"
        height={GAUGE_SIZE}
        viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}
        width={GAUGE_SIZE}
      >
        <g transform={`rotate(-90 ${GAUGE_SIZE / 2} ${GAUGE_SIZE / 2})`}>
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            fill="none"
            r={GAUGE_RADIUS}
            stroke="#ECEEFF"
            strokeWidth={GAUGE_STROKE}
          />
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            fill="none"
            r={GAUGE_RADIUS}
            stroke="#8080FF"
            strokeDasharray={`${dash} ${GAUGE_CIRCUMFERENCE - dash}`}
            strokeLinecap={clamped > 0 && clamped < 100 ? 'round' : 'butt'}
            strokeWidth={GAUGE_STROKE}
          />
        </g>
      </svg>
      <span className={styles.gaugeCenter}>
        <span className={styles.gaugeScore}>{Math.round(score)}</span>
        <span className={styles.gaugeOutOf}>/100</span>
      </span>
    </div>
  )
}

const StackedBar: FC<{
  segments: { color: string; key: string; value: number }[]
}> = ({ segments }) => {
  const total = segments.reduce((acc, segment) => acc + segment.value, 0)
  return (
    <div className={styles.stackedBar}>
      {total > 0 &&
        segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <span
              className={styles.stackedSegment}
              key={segment.key}
              style={{
                background: segment.color,
                width: `${(segment.value / total) * 100}%`,
              }}
            />
          ))}
    </div>
  )
}

const EmptyNote: FC<{ text: string }> = ({ text }) => (
  <div className={styles.emptyNote}>{text}</div>
)

const SentimentCard: FC<{ loading: boolean; overview?: ContentOverviewV2 }> = ({
  loading,
  overview,
}) => {
  const sentiment = overview?.sentiment
  const score = sentiment?.score
  const total = sentiment?.total ?? 0
  // Comments exist but none has been through Run analysis yet: every one
  // reads as neutral, and the score that comes back (0, "Poor") is about
  // nothing. Checked ahead of the score so it wins whatever the score is.
  const notAnalysed = total > 0 && (sentiment?.analyzed ?? 0) === 0
  const hasScore = typeof score === 'number' && total > 0 && !notAnalysed
  const band = hasScore ? sentiment?.label ?? scoreBand(score as number) : null
  const platforms = [...(sentiment?.byPlatform ?? [])]
    .filter((entry) => entry.total > 0)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))

  return (
    <div className={`${styles.card} ${styles.sentimentCard}`}>
      <CardTitle icon={SentimentFaceIcon} title="Sentiment Score">
        {band && !notAnalysed && (
          <span className={`${styles.bandPill} ${BAND_CLASS[band]}`}>
            {band}
          </span>
        )}
        {/* The score's change is in points, not percent. */}
        {hasScore && <Delta suffix=" pts" value={sentiment?.delta} />}
      </CardTitle>

      {loading ? (
        <>
          <div className={styles.scoreRow}>
            <Skeleton circle width={GAUGE_SIZE} />
            <div className={styles.scoreText}>
              <Skeleton height={40} width="100%" />
              <Skeleton height={12} radius={6} width="100%" />
              <Skeleton height={14} width={220} />
            </div>
          </div>
          <Skeleton height={130} radius={12} width="100%" />
        </>
      ) : notAnalysed ? (
        <div className={styles.notAnalysed}>
          <span className={styles.notAnalysedTitle}>
            Sentiment not analysed yet
          </span>
          <span className={styles.notAnalysedText}>
            {`${formatCount(total)} ${
              total === 1 ? 'comment is' : 'comments are'
            } waiting for sentiment analysis. The score appears once it has run.`}
          </span>
        </div>
      ) : !hasScore ? (
        <EmptyNote text="No comments in this period" />
      ) : (
        <>
          <div className={styles.scoreRow}>
            <Gauge score={score as number} />
            <div className={styles.scoreText}>
              <p className={styles.sentence}>{SCORE_SENTENCE[band!]}</p>
              <StackedBar
                segments={[
                  {
                    color: SENTIMENT_COLORS.positive,
                    key: 'positive',
                    value: sentiment!.positive,
                  },
                  {
                    color: SENTIMENT_COLORS.neutral,
                    key: 'neutral',
                    value: sentiment!.neutral,
                  },
                  {
                    color: SENTIMENT_COLORS.negative,
                    key: 'negative',
                    value: sentiment!.negative,
                  },
                ]}
              />
              <div className={styles.legend}>
                {(['positive', 'neutral', 'negative'] as const).map((key) => (
                  <span className={styles.legendItem} key={key}>
                    <span
                      className={styles.legendDot}
                      style={{ background: SENTIMENT_COLORS[key] }}
                    />
                    {`${key.charAt(0).toUpperCase()}${key.slice(1)} ${percent(
                      sentiment![key],
                      total
                    )}%`}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.byPlatform}>
            <div className={styles.byPlatformHeader}>
              <span className={styles.byPlatformTitle}>Score by platform</span>
              <span className={styles.basedOn}>
                <CommentIcon className={styles.basedOnIcon} />
                {`Based on ${formatCount(sentiment!.analyzed)} analyzed ${
                  sentiment!.analyzed === 1 ? 'comment' : 'comments'
                }`}
              </span>
            </div>
            {platforms.map((entry) => (
              <div className={styles.platformScoreRow} key={entry.platform}>
                <span className={styles.platformScoreName}>
                  {platformLabel(entry.platform)}
                </span>
                <SentimentHoverCard
                  className={styles.platformScoreBar}
                  sentiment={entry}
                  total={entry.total}
                  totalLabel="Total Comments"
                >
                  {/* Full width: the bar is the platform's comment mix and
                      the number beside it is the score. Running the bar to
                      the score left a sliver for poor scores (7 → 7%). */}
                  <SentimentBar fill={1} flat sentiment={entry} />
                </SentimentHoverCard>
                <span className={styles.platformScoreValue}>
                  {entry.score === null ? '–' : Math.round(entry.score)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const visiblePlatforms = (items: ContentPlatformCount[] = []) =>
  items.filter((entry) => entry.count > 0).sort((a, b) => b.count - a.count)

const PostsCard: FC<{ loading: boolean; overview?: ContentOverviewV2 }> = ({
  loading,
  overview,
}) => {
  const posts = overview?.posts
  const platforms = visiblePlatforms(posts?.byPlatform)

  return (
    <div className={styles.card}>
      <CardTitle icon={NewspaperIcon} title="Total Posts">
        <Delta value={posts?.delta} />
      </CardTitle>
      {loading ? (
        <>
          <Skeleton height={36} width={120} />
          <Skeleton height={8} radius={4} width="100%" />
          <Skeleton height={36} width="100%" />
        </>
      ) : !posts?.total ? (
        <EmptyNote text="No posts in this period" />
      ) : (
        <>
          <div className={styles.totalRow}>
            <span className={styles.bigNumber}>{formatCount(posts.total)}</span>
            <span className={styles.platformsPill}>
              {`${platforms.length} ${
                platforms.length === 1 ? 'Platform' : 'Platforms'
              }`}
            </span>
          </div>
          <StackedBar
            segments={platforms.map((entry, index) => ({
              color: platformColor(entry.platform, index),
              key: entry.platform,
              value: entry.count,
            }))}
          />
          <div className={styles.platformColumns}>
            {platforms.map((entry, index) => (
              <div className={styles.platformColumn} key={entry.platform}>
                <span className={styles.platformColumnLabel}>
                  <span
                    className={styles.legendDot}
                    style={{ background: platformColor(entry.platform, index) }}
                  />
                  {platformLabel(entry.platform)}
                </span>
                <span className={styles.platformColumnValue}>
                  {formatCount(entry.count)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const CommentsCard: FC<{ loading: boolean; overview?: ContentOverviewV2 }> = ({
  loading,
  overview,
}) => {
  const comments = overview?.comments
  const platforms = visiblePlatforms(comments?.byPlatform)
  const total = platforms.reduce((acc, entry) => acc + entry.count, 0)

  let offset = 0
  const arcs = platforms.map((entry, index) => {
    const length = total > 0 ? (entry.count / total) * DONUT_CIRCUMFERENCE : 0
    const arc = {
      color: platformColor(entry.platform, index),
      dash: length,
      key: entry.platform,
      start: offset,
    }
    offset += length
    return arc
  })

  if (loading) {
    return (
      <div className={`${styles.card} ${styles.commentsCard}`}>
        <Skeleton circle width={DONUT_SIZE} />
        <div className={styles.commentsLegend}>
          {Array.from({ length: 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton height={18} key={index} width="100%" />
          ))}
        </div>
      </div>
    )
  }

  if (!comments?.total) {
    return (
      <div className={styles.card}>
        <CardTitle icon={CommentIcon} title="Total Comments" />
        <EmptyNote text="No comments in this period" />
      </div>
    )
  }

  return (
    <div className={`${styles.card} ${styles.commentsCard}`}>
      <div className={styles.donut}>
        <svg
          aria-hidden="true"
          height={DONUT_SIZE}
          viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
          width={DONUT_SIZE}
        >
          <g transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}>
            {arcs.map((arc) => {
              const gap =
                arcs.length > 1 ? Math.min(DONUT_GAP, arc.dash / 2) : 0
              const dash = Math.max(0, arc.dash - gap)
              return (
                <circle
                  cx={DONUT_SIZE / 2}
                  cy={DONUT_SIZE / 2}
                  fill="none"
                  key={arc.key}
                  r={DONUT_RADIUS}
                  stroke={arc.color}
                  strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
                  strokeDashoffset={-(arc.start + gap / 2)}
                  strokeWidth={DONUT_STROKE}
                />
              )
            })}
          </g>
        </svg>
        <span className={styles.donutCenter}>
          <span className={styles.donutLabel}>Total Comments</span>
          <span className={styles.donutValue}>
            {formatCount(comments.total)}
          </span>
          <Delta value={comments.delta} />
        </span>
      </div>
      <div className={styles.commentsLegend}>
        {platforms.map((entry, index) => (
          <div className={styles.commentsLegendRow} key={entry.platform}>
            <span className={styles.platformColumnLabel}>
              <span
                className={styles.legendDot}
                style={{ background: platformColor(entry.platform, index) }}
              />
              {platformLabel(entry.platform)}
            </span>
            <span className={styles.commentsLegendValue}>
              {formatCount(entry.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface TopColumnData {
  // Shown instead of "No data" when the value is missing.
  empty?: string
  label: string
  negative?: boolean
  sub?: string
  value?: string | null
}

const TopCard: FC<{
  columns: TopColumnData[]
  icon: Icon
  loading: boolean
  title: string
}> = ({ columns, icon, loading, title }) => (
  <div className={`${styles.card} ${styles.topCard}`}>
    <CardTitle icon={icon} title={title} />
    <div
      className={`${styles.topColumns} ${
        columns.length === 1 ? styles.topColumnsSingle : ''
      }`}
    >
      {columns.map((column) => (
        <div className={styles.topColumn} key={column.label}>
          <span className={styles.topLabel}>
            <span
              className={`${styles.topDot} ${
                column.negative ? styles.topDotNegative : ''
              }`}
            />
            {column.label}
          </span>
          {loading ? (
            <Skeleton height={20} width="80%" />
          ) : column.value ? (
            <>
              <span className={styles.topValue} title={column.value}>
                {column.value}
              </span>
              {column.sub && (
                <span className={styles.topSub}>{column.sub}</span>
              )}
            </>
          ) : (
            <span className={styles.topNoData}>
              {column.empty ?? 'No data'}
            </span>
          )}
        </div>
      ))}
    </div>
  </div>
)

const commentsLabel = (count: number) =>
  `${formatCount(count)} ${count === 1 ? 'comment' : 'comments'}`

const postsLabel = (count: number) =>
  `${formatCount(count)} ${count === 1 ? 'post' : 'posts'}`

type Overview = ContentOverviewV2

const scoreSub = (comments: number, score: number | null) =>
  [
    commentsLabel(comments),
    score === null ? null : `score ${Math.round(score)}`,
  ]
    .filter(Boolean)
    .join(' · ')

// Most Positive / Most Negative as drawn, when the read carries them; the
// most discussed analysis alone when it does not (older responses).
const campaignColumns = (
  top: Overview['topCampaign'] | undefined
): TopColumnData[] => {
  // With one analysis carrying comments it is both the most positive and the
  // most negative; naming it twice says nothing, so it is shown once.
  const same =
    !!top?.mostPositive &&
    top.mostPositive.analysisId === top.mostNegative?.analysisId
  if (top && !same && (top.mostPositive || top.mostNegative)) {
    return [
      {
        label: 'Most Positive',
        sub: top.mostPositive
          ? scoreSub(top.mostPositive.comments, top.mostPositive.score)
          : undefined,
        value: top.mostPositive?.title,
      },
      {
        label: 'Most Negative',
        negative: true,
        sub: top.mostNegative
          ? scoreSub(top.mostNegative.comments, top.mostNegative.score)
          : undefined,
        value: top.mostNegative?.title,
      },
    ]
  }
  return [
    {
      label: 'Most Discussed',
      sub: top ? scoreSub(top.comments, top.score) : undefined,
      value: top?.title,
    },
  ]
}

const dayColumns = (top: Overview['topDay'] | undefined): TopColumnData[] => {
  if (top && (top.mostPositive || top.mostNegative)) {
    return [
      {
        label: 'Most Positive',
        sub: top.mostPositive
          ? commentsLabel(top.mostPositive.comments)
          : undefined,
        value: top.mostPositive?.label,
      },
      {
        label: 'Most Negative',
        negative: true,
        sub: top.mostNegative
          ? commentsLabel(top.mostNegative.comments)
          : undefined,
        value: top.mostNegative?.label,
      },
    ]
  }
  return [
    {
      label: 'Most Posts',
      sub: top
        ? `${postsLabel(top.posts)} · ${commentsLabel(top.comments)}`
        : undefined,
      value: top?.label,
    },
  ]
}

const platformSub = (entry: ContentTopPlatform) =>
  [
    commentsLabel(entry.comments),
    typeof entry.posts === 'number' ? postsLabel(entry.posts) : null,
  ]
    .filter(Boolean)
    .join(' · ')

const platformColumns = (
  top: Overview['topPlatform'] | undefined
): TopColumnData[] => [
  {
    label: 'Most Active',
    sub: top ? platformSub(top.most) : undefined,
    value: top ? platformLabel(top.most.platform) : null,
  },
  // One platform in scope is both; it is shown once.
  ...(top && top.least.platform === top.most.platform
    ? []
    : [
        {
          label: 'Least Active',
          negative: true,
          sub: top ? platformSub(top.least) : undefined,
          value: top ? platformLabel(top.least.platform) : null,
        },
      ]),
]

const formatErr = (err: number) =>
  `${Number.isInteger(err) ? err : err.toFixed(1)}%`

export const ContentAnalysisOverview: FC<ContentAnalysisOverviewProps> = ({
  analysisIds,
  campaigns,
  range,
  variant,
}) => {
  const { data, isError, isFetching, isLoading } =
    useGetContentAnalysisV2OverviewQuery({ ...range, analysisIds })
  const loading = isLoading && !data
  // keepPreviousData holds the last range on screen while the next one loads;
  // dim it rather than flash skeletons.
  const refetching = isFetching && !isLoading
  const overview = data

  const topPlatform = overview?.topPlatform
  const topDay = overview?.topDay
  const topCampaign = overview?.topCampaign
  const topContentType = overview?.topContentType

  return (
    <section className={styles.overview}>
      <div className={styles.header}>
        <h2 className={styles.title}>Overview</h2>
        {campaigns && (
          <span className={styles.campaigns}>
            Campaigns
            <strong>{`${campaigns.active} active`}</strong>
            <span>{`/ ${campaigns.total} total`}</span>
          </span>
        )}
      </div>

      {isError && !data ? (
        <div className={styles.errorState}>
          The overview could not be loaded. Please try again later.
        </div>
      ) : (
        <div className={`${styles.grid} ${refetching ? styles.dimmed : ''}`}>
          <SentimentCard loading={loading} overview={overview} />
          <div className={styles.rightColumn}>
            <PostsCard loading={loading} overview={overview} />
            <CommentsCard loading={loading} overview={overview} />
          </div>

          <div className={styles.topRow}>
            {variant === 'list' ? (
              <TopCard
                columns={campaignColumns(topCampaign)}
                icon={CampaignIcon}
                loading={loading}
                title="Top Campaign"
              />
            ) : (
              <TopCard
                columns={[
                  {
                    // A type needs at least 3 posts in range to be ranked.
                    empty: 'Not enough posts yet',
                    label: 'Best Performing',
                    sub: topContentType
                      ? postsLabel(topContentType.posts)
                      : undefined,
                    value: topContentType
                      ? `${topContentType.label}, ERR ${formatErr(
                          topContentType.err
                        )}`
                      : null,
                  },
                ]}
                icon={ContentTypeIcon}
                loading={loading}
                title="Top Content Type"
              />
            )}
            <TopCard
              columns={dayColumns(topDay)}
              icon={CalendarIcon}
              loading={loading}
              title="Top Day"
            />
            <TopCard
              columns={platformColumns(topPlatform)}
              icon={GlobeIcon}
              loading={loading}
              title="Top Platform"
            />
          </div>
        </div>
      )}
    </section>
  )
}
